import { Dropdown } from 'primereact/dropdown';
import { Fieldset } from 'primereact/fieldset';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import moduleService from './module-service';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import BreadcrumbComponent from '../../../../components/Breadcrumb';


interface DropdownItem {
    fullDesc: string;
    referenceCode: string;
}

const currentDate = new Date().toLocaleString("en-US", { timeZone: "Asia/Kuala_Lumpur" });

const ModuleEdit: React.FC = () => {

    const location = useLocation();
    const navigate = useNavigate();
    const moduleData = location.state?.module;
    const isEditMode = !!moduleData;
    const toast = useRef<Toast>(null);

    const [loading, setLoading] = useState(false);
    const [clientState, setClientState] = useState<any>(null);
    const [formData, setFormData] = useState<any>({});
    const [departmentItems, setDepartmentItems] = useState<DropdownItem[]>([]);
    const dropdownDepartmentItems = useMemo(() => departmentItems, [departmentItems]);
    const [errors, setErrors] = useState<any>({});
    useEffect(() => {
        const fetchClientState = async () => {
            try {
                const clientStateItem = await moduleService.getClientState();
                setClientState(clientStateItem);

                const deptData = await moduleService.getDeptDropdown();
                setDepartmentItems(deptData);

                if (isEditMode) {
                    setFormData(moduleData)
                }

            } catch (error) {
                console.error('Error fetching client state:', error);
            }
        };

        fetchClientState();
    }, []);

    const validate = () => {
        let valid = true;

        if (!formData.moduleId) {
            errors.moduleId = 'Module ID is required';
            valid = false;
        }
        if (!formData.fullDesc) {
            errors.fullDesc = 'Full Description is required';
            valid = false;
        }
        if (!formData.shortDesc) {
            errors.shortDesc = 'Short Description is required';
            valid = false;
        }
        if (!formData.orderSeq) {
            errors.orderSeq = 'Order Sequence is required';
            valid = false;
        }
        setFormData({ ...formData, errors });
        return valid;
    }

    const handleInputChange = (e: any) => {
        const { id, value } = e.target;
        setFormData({
            ...formData,
            [id]: value.toUpperCase()
        });
    };

    const handleDropdownChange = (e: any) => {
        const { id, value } = e.target;
        setFormData({
            ...formData,
            [id]: value
        });
    };

    const formatDate = (dateString: any) => {
        const date = new Date(dateString);
        return date.toLocaleString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hourCycle: "h23",
            timeZone: "Asia/Kuala_Lumpur",
        }).replace(", ", " ");
    };

    const toolbarTemplate = () => {
        function handleBack(): void {
            navigate('/admin/module');
        }
        function handleSave(event: React.FormEvent): void {
            event.preventDefault();

            if (validate()) {

                setLoading(true);

                if (isEditMode) {
                    formData.modifiedBy = clientState.userId;
                    formData.modifiedDate = currentDate;
                } else {
                    formData.insertBy = clientState.userId;
                    formData.insertDate = currentDate;
                    formData.modifiedBy = clientState.userId;
                    formData.modifiedDate = currentDate;
                }

                if (isEditMode) {
                    moduleService.update(formData.moduleId, formData).then((response) => {
                        const data = response.data;
                        clientState.success.detail = 'Already Save Record';
                        toast.current?.show(clientState.success);
                        navigate("/admin/module");
                    }).catch((error) => {
                        console.error('Error updating module:', error);
                        toast.current?.show({ severity: 'error', summary: 'Update Module', detail: 'Error updating module' });
                    }).finally(() => {
                        setLoading(false);
                    });
                } else {
                    moduleService.create(formData).then((response) => {
                        const data = response.data;
                        clientState.success.detail = 'Already Save Record';
                        toast.current?.show(clientState.success);
                        navigate("/admin/module");
                    });
                }
            }


        }

        return (
            <>
                <Toast ref={toast} />
                <Button label="Save" icon="pi pi-check" style={{ marginRight: '.5em' }} onClick={handleSave} disabled={loading}>

                </Button>
                {/* <Button label="Delete" icon="pi pi-trash" style={{ marginRight: '.5em' }} /> */}
                <Button label="Back" icon="pi pi-times" style={{ marginRight: '.5em' }} onClick={handleBack}>

                </Button>

            </>
        );
    };

    return (
        <div className='grid'>
            <div className='col-12'>
                <div className='card-title'>
                    <h5>Module Form</h5>
                    <BreadcrumbComponent />
                </div>
                <div className='form-title'>
                    <Fieldset legend="Module Information">
                        <div className="p-fluid form-grid grid">
                            <div className="field col-12 md:col-6">
                                <label>Module ID</label>
                                <InputText
                                    id="moduleId"
                                    value={formData.moduleId}
                                    onChange={handleInputChange}
                                />
                                {errors.moduleId && <small className="p-error">{errors.moduleId}</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>Full Description</label>
                                <InputText
                                    id="fullDesc"
                                    value={formData.fullDesc}
                                    onChange={handleInputChange}
                                />
                                {errors.fullDesc && <small className="p-error">{errors.fullDesc}</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>Short Description</label>
                                <InputText
                                    id="shortDesc"
                                    value={formData.shortDesc}
                                    onChange={handleInputChange}
                                />
                                {errors.shortDesc && <small className="p-error">{errors.shortDesc}</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label htmlFor="departmentCode">Department</label>
                                <Dropdown
                                    id="departmentCode"
                                    value={formData.departmentCode}
                                    onChange={handleDropdownChange}
                                    options={dropdownDepartmentItems}
                                    optionLabel="fullDesc"
                                    placeholder="Select One"
                                    optionValue="departmentCode"
                                >
                                </Dropdown>
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>Order Sequence</label>
                                <InputText
                                    id="orderSeq"
                                    value={formData.orderSeq}
                                    onChange={handleInputChange}
                                />
                                {errors.orderSeq && <small className="p-error">{errors.orderSeq}</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <h6>Status</h6>
                                <InputSwitch
                                    checked={formData.recordStatus === "1"}
                                    onChange={(e) =>
                                        setFormData({ ...formData, recordStatus: e.value ? "1" : "0" })
                                    }
                                />
                            </div>
                            <div className="field col-12">
                                <label htmlFor="remark">Remarks</label>
                                <InputTextarea
                                    id="remark"
                                    value={formData.remark}
                                    onChange={(e) =>
                                        setFormData({ ...formData, remark: e.target.value })
                                    }
                                    rows={4} />
                            </div>
                            {moduleData && (
                                <>
                                    <div className="field col-12 md:col-6">
                                        <label>Record Created By</label>
                                        <div>{moduleData?.insertByHasOne?.fullName} @ {formatDate(moduleData?.insertDate)}</div>
                                    </div>
                                    <div className="field col-12 md:col-6">
                                        <label>Record Updated By</label>
                                        <div >{moduleData?.modifiedByHasOne?.fullName} @ {formatDate(moduleData?.modifiedDate)}</div>
                                    </div>
                                </>
                            )}
                        </div>
                    </Fieldset>
                    <div>
                        <br />
                    </div>
                    <Toolbar end={toolbarTemplate}></Toolbar>
                </div>
            </div>
        </div>
    );
};

export default ModuleEdit;